import React, { useEffect, useMemo, useState } from 'react';
import { Grid2, MenuItem, Switch, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Controller, useFormContext } from 'react-hook-form';
import { COLORS } from '../../../utils/Application_Constants';
import ExpenseTextField from './ExpenseTextField';
import ExpenseTransactionDate from './ExpenseTransactionDate';
import ExpenseDateRange from './ExpenseDateRange';
import ExpensePostedDate from './ExpensePostedDate';
import ExpenseCategoryType from './ExpenseCategoryType';
import ExpenseBillType from './ExpenseBillType';
import ExpenseAmount from './ExpenseAmount';
import ExpenseComments from './ExpenseComments';
import ExpensePayDate from './ExpensePayDate';
import { TypeEnum } from './Division.enum';
import { ExpenseClassEnum } from './ExpenseClass.enum';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import ExpenseDescription from './ExpenseDescription';
import { CreditCardCategoryEnum } from './CreditCardCategory.enum';
import AllocatedAmount from './AllocatedAmount';
import { ExpenseCategoryEnum } from './ExpenseCategory.enum';
import { ExpenseTypeEnum } from './ExpenseType.enum';
import MiscExpenseDescription from './MiscExpenseDescription';
import { ExpenseModeEnum } from './ExpenseMode.enum';
import useAdminAuthContext from '../../../Admin/hooks/useAdminAuthContext';
import CopyIconButtonWithTooltip from '../CopyIconButtonWithTooltip';

const styles = () => ({
    formHeader: {
        borderBottom: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        paddingBottom: 8,
        marginBottom: 16,
    },
});

const PreCodedExpenseDetailsForm = (props) => {
    const {
        allCreditCards,
        classes,
        divisions,
        expense,
        expenseCategoriesSGA,
        expenseCategoriesNonSGA,
        index,
        isViewOnly,
        isInvoiceReviewLocked = false,
        isSGA,
        mode,
        originalExpense,
        setIsSGA,
        isCreditCardTransaction = false,
    } = props;
    const [required, setRequired] = useState({ required: mode !== ExpenseModeEnum.CreditCard });
    const { control, watch, errors, setValue, getValues, clearErrors } = useFormContext();
    const [expenseCategories, setExpenseCategories] = useState(null);
    const expenseCategory = watch(`expenseEntries[${index}].category`);
    const expenseType = watch(`expenseEntries[${index}].type`);
    const billType = watch(`expenseEntries[${index}].billType`);
    const currentStatus = watch(`expenseEntries[${index}].status`);
    const creditCardFieldsRequired =
        mode !== ExpenseModeEnum.CreditCard ||
        currentStatus === ExpenseStatusValuesEnum.Approved ||
        currentStatus === ExpenseStatusValuesEnum.InvoiceReview;
    const hasPayDate =
        billType && billType === 'ClinicianDeduction' && mode === ExpenseModeEnum.CreditCard;
    const isExpenseTravel = expenseCategory && expenseCategory === ExpenseCategoryEnum.Travel;
    const isMiscExpense =
        expenseCategory === ExpenseCategoryEnum.Misc && expenseType === ExpenseTypeEnum.Misc;
    const canRemoveTravelDateFields =
        expenseType === ExpenseTypeEnum.BaggageFee ||
        expenseType === ExpenseTypeEnum.Fuel ||
        expenseType === '';
    const hasTransactionDate = expenseCategory && expenseCategory !== '';
    const [isDivisionDisabled, setIsDivisionDisabled] = useState(false);
    const [isCorrectionExpense, setIsCorrectionExpense] = useState(false);
    const isClinicianReimbursement = false;
    const importedData = watch(`expenseEntries[0].importedData`);
    const parentExpenseErrors = watch(`expenseEntries[0].errors`);
    const isPosted = expense.status === ExpenseStatusValuesEnum.Posted;
    const merchantNameKeys = ['Supplier', 'Supplier Name', 'Merchant Name'];
    const isMerchantNameInImportedData =
        importedData && merchantNameKeys.some((key) => importedData[key]);
    const { isCreditCardUser } = useAdminAuthContext();

    useEffect(() => {
        setRequired({
            required:
                mode !== ExpenseModeEnum.CreditCard ||
                expense?.status === ExpenseStatusValuesEnum.Approved ||
                expense?.status === ExpenseStatusValuesEnum.InvoiceReview,
        });
    }, [expense?.status]);

    useEffect(() => {
        setIsCorrectionExpense(
            expense &&
                (expense?.status === ExpenseStatusValuesEnum.InCorrection ||
                    (expense?.primaryExpenseId && expense?.primaryExpenseId !== '') ||
                    expense?.previousVersionId)
        );
    }, [expense]);

    const expenseTypes = useMemo(() => {
        if (!expenseCategory) return [];
        if (isSGA[index]) {
            if (!expenseCategoriesSGA) return [];
            let { types } = expenseCategoriesSGA.find(
                (option) => option.category === expenseCategory
            );
            types = types.filter((type) => type.name !== ExpenseTypeEnum.Mileage);
            return types || [];
        }
        if (!expenseCategoriesNonSGA) return [];
        let { types } = expenseCategoriesNonSGA.find(
            (option) => option.category === expenseCategory
        );
        types = types.filter((type) => type.name !== ExpenseTypeEnum.Mileage);
        return types || [];
    }, [expenseCategory, expenseCategoriesSGA, expenseCategoriesNonSGA]);

    const toggleIsSGA = () => {
        // Backup Form Settings
        setValue(`expenseEntries[${index}].sgaType`, getValues(`expenseEntries[${index}].type`));
        setValue(
            `expenseEntries[${index}].sgaDivision`,
            getValues(`expenseEntries[${index}].division`)
        );
        setValue(
            `expenseEntries[${index}].sgaCategory`,
            getValues(`expenseEntries[${index}].category`)
        );
        setValue(
            `expenseEntries[${index}].sgaBillType`,
            getValues(`expenseEntries[${index}].billType`)
        );
        setValue(
            `expenseEntries[${index}].sgaExpenseClass`,
            getValues(`expenseEntries[${index}].expenseClass`)
        );
        // Blank Form Values
        setValue(`expenseEntries[${index}].type`, '');
        setValue(`expenseEntries[${index}].division`, '');
        setValue(`expenseEntries[${index}].category`, '');
        setValue(`expenseEntries[${index}].billType`, '');
        setValue(`expenseEntries[${index}].expenseClass`, '');
        isSGA[index] = !isSGA[index];
        setIsSGA(isSGA);
        clearErrors(`expenseEntries[${index}]`);
    };

    const checkIsDisabledDivision = (aExpense) => {
        const disabledValues = [
            TypeEnum.CustomerCareGiftsFlowers,
            TypeEnum.MalpracticeCompFundStateCompfunds,
            TypeEnum.PSYCMalpracticeCompFund,
            TypeEnum.UnidentifiedLicensing,
            TypeEnum.LodgingSecurityDeposit,
            TypeEnum.AssociateTravelPrepaid,
            TypeEnum.AssociateTravelAccrued,
            TypeEnum.Disputed,
            TypeEnum.IncidentalsRounding,
            TypeEnum.Rebate,
        ];
        if (disabledValues.includes(aExpense.type)) {
            setIsDivisionDisabled(true);
        } else {
            setIsDivisionDisabled(false);
        }
    };

    useEffect(async () => {
        if (isSGA[index]) {
            setExpenseCategories(expenseCategoriesSGA);
        } else {
            setExpenseCategories(expenseCategoriesNonSGA);
        }
        checkIsDisabledDivision(expense);
    }, [expenseCategory, expenseCategoriesSGA, expenseCategoriesNonSGA]);

    useEffect(() => {
        if (mode === ExpenseModeEnum.CreditCard) {
            console.log(isSGA[index], `- PreCodedExpenseDetailsForm isSGA ${index} Has changed`);
            if (isSGA[index]) {
                setExpenseCategories(expenseCategoriesSGA);
                setValue(
                    `expenseEntries[${index}].type`,
                    getValues(`expenseEntries[${index}].sgaType`)
                );
                setValue(
                    `expenseEntries[${index}].division`,
                    getValues(`expenseEntries[${index}].sgaDivision`)
                );
                setValue(
                    `expenseEntries[${index}].category`,
                    getValues(`expenseEntries[${index}].sgaCategory`)
                );
                setValue(
                    `expenseEntries[${index}].billType`,
                    getValues(`expenseEntries[${index}].sgaBillType`)
                );
                setValue(
                    `expenseEntries[${index}].expenseClass`,
                    ExpenseClassEnum.SalesGeneralAndAdmin
                );
            } else {
                setExpenseCategories(expenseCategoriesNonSGA);
                setValue(
                    `expenseEntries[${index}].type`,
                    getValues(`expenseEntries[${index}].bookingType`)
                );
                setValue(
                    `expenseEntries[${index}].division`,
                    getValues(`expenseEntries[${index}].bookingDivision`)
                );
                setValue(
                    `expenseEntries[${index}].category`,
                    getValues(`expenseEntries[${index}].bookingCategory`)
                );
                setValue(
                    `expenseEntries[${index}].billType`,
                    getValues(`expenseEntries[${index}].bookingBillType`)
                );
                setValue(
                    `expenseEntries[${index}].expenseClass`,
                    ExpenseClassEnum.IngestedCCTransaction
                );
            }
        }
    }, [isSGA[index]]);

    const updateAllAmounts = React.useCallback(
        (value) => {
            if (mode === ExpenseModeEnum.CreditCard) {
                const entries = getValues('expenseEntries') || [];
                for (let i = 0; i < entries.length; i++) {
                    setValue(`expenseEntries[${i}].amount`, value, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                    });
                }
            }
        },
        [getValues, setValue]
    );

    const isParentExpenseEntry = index === 0;
    const shouldShowCopyChargeAmount =
        isParentExpenseEntry && isCreditCardTransaction && !isViewOnly && !isInvoiceReviewLocked;
    const chargeAmountValue = watch(`expenseEntries[${index}].amount`);
    const copyChargeAmountIntoAllocatedAmount = () => {
        if (chargeAmountValue) {
            setValue(`expenseEntries[${index}].allocatedAmount`, chargeAmountValue, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    };

    return (
        <>
            <Typography variant="h5" component="h5" className={classes.formHeader}>
                <Grid2 container spacing={3}>
                    <Grid2 item size={{ xs: 12, md: 6 }}>
                        Expense Details
                    </Grid2>
                    <Grid2 item size={{ xs: 12, md: 6 }}>
                        {mode === ExpenseModeEnum.CreditCard && isSGA[index] && (
                            <>
                                Booking
                                <Switch
                                    checked={isSGA[index]}
                                    onChange={toggleIsSGA}
                                    color="primary"
                                    inputProps={{ 'aria-label': 'primary checkbox' }}
                                    disabled={
                                        (isPosted && isViewOnly) ||
                                        isViewOnly ||
                                        isInvoiceReviewLocked
                                    }
                                />
                                SG&A
                            </>
                        )}
                    </Grid2>
                </Grid2>
            </Typography>
            <Grid2 container spacing={3}>
                {Boolean(expense.id) && (
                    <Grid2 item size={{ xs: 12, md: 6 }}>
                        <ExpenseTextField
                            id="expense-id"
                            label="Expense ID"
                            value={String(expense.id)}
                            disabled
                        />
                    </Grid2>
                )}
                {mode === ExpenseModeEnum.CreditCard && isSGA[index] && (
                    <>
                        <Grid2 item size={{ xs: 12, md: 6 }}>
                            <Controller
                                control={control}
                                isViewOnly={isViewOnly}
                                name={`expenseEntries[${index}].division`}
                                defaultValue={!expense.division ? '' : expense.division}
                                rules={{ required: 'Division is required' }}
                                render={({ onChange, value }) => (
                                    <ExpenseTextField
                                        id="division"
                                        select
                                        onChange={(e) => {
                                            onChange(e);
                                            setValue(
                                                `expenseEntries[${index}].sgaDivision`,
                                                e.target.value
                                            );
                                        }}
                                        value={!value ? '' : value}
                                        label="Division"
                                        required
                                        disabled={
                                            isDivisionDisabled ||
                                            (isPosted && isViewOnly) ||
                                            isViewOnly
                                        }
                                        error={Boolean(errors.expenseEntries?.[index]?.division)}
                                        helperText={
                                            errors.expenseEntries?.[index]?.division?.message
                                        }
                                    >
                                        <MenuItem value="" disabled>
                                            Select a division
                                        </MenuItem>
                                        {divisions.map((option, i) => (
                                            <MenuItem
                                                key={option.name}
                                                value={option.name}
                                                id={`division-menu-option-${i}`}
                                            >
                                                {option.description}
                                            </MenuItem>
                                        ))}
                                    </ExpenseTextField>
                                )}
                            />
                        </Grid2>
                        <Grid2 item size={{ xs: 12, md: 6 }} />
                    </>
                )}
                <Grid2 item size={{ xs: 12, md: 6 }}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].creditCardId`}
                        isViewOnly={isViewOnly}
                        defaultValue={expense.creditCardId}
                        rules={{
                            required: required.required ? 'Credit card acct is required' : false,
                        }}
                        render={({ onChange, value }) => {
                            let hasFailedToMatchCC = true;
                            if (parentExpenseErrors && Array.isArray(parentExpenseErrors)) {
                                hasFailedToMatchCC = parentExpenseErrors.some(
                                    (error) =>
                                        error.error ===
                                        'Failed to match Credit Card using Last Four Digits'
                                );
                            }

                            return (
                                <ExpenseTextField
                                    id="credit-card-acct"
                                    select
                                    onChange={onChange}
                                    value={value}
                                    label="Credit card acct"
                                    {...required}
                                    disabled={
                                        !hasFailedToMatchCC ||
                                        (isPosted && isViewOnly) ||
                                        isViewOnly
                                    }
                                    error={Boolean(errors.expenseEntries?.[index]?.creditCardId)}
                                    helperText={
                                        errors.expenseEntries?.[index]?.creditCardId?.message
                                    }
                                >
                                    <MenuItem value="" disabled>
                                        Select a credit card account
                                    </MenuItem>
                                    {allCreditCards.map((creditcard) => (
                                        <MenuItem
                                            key={creditcard.id}
                                            id={`credit-card-acct-menu-option-${creditcard.id}`}
                                            value={creditcard.id}
                                        >
                                            {CreditCardCategoryEnum[creditcard.category]}-
                                            {creditcard.name}-{creditcard.lastFourDigits}
                                        </MenuItem>
                                    ))}
                                </ExpenseTextField>
                            );
                        }}
                    />
                </Grid2>
                <Grid2 item size={{ xs: 12, md: 6 }}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].merchantName`}
                        defaultValue={expense.merchantName}
                        rules={{
                            required: required.required ? 'Merchant name is required' : false,
                        }}
                        render={({ onChange, value }) => (
                            <ExpenseTextField
                                id="merchant-name"
                                onChange={onChange}
                                inputProps={{ maxLength: 50 }}
                                value={value}
                                placeholder="Merchant name"
                                label="Merchant name"
                                {...required}
                                error={Boolean(errors.expenseEntries?.[index]?.merchantName)}
                                helperText={errors.expenseEntries?.[index]?.merchantName?.message}
                                disabled={
                                    isMerchantNameInImportedData ||
                                    isInvoiceReviewLocked ||
                                    (isPosted && isViewOnly) ||
                                    isViewOnly
                                }
                            />
                        )}
                    />
                </Grid2>
                <ExpenseBillType
                    index={index}
                    expense={expense}
                    isClinicianReimbursement={isClinicianReimbursement}
                    mode={mode}
                    isSGA={isSGA}
                    isPosted={isPosted}
                    isViewOnly={isViewOnly}
                    isInvoiceReviewLocked={isInvoiceReviewLocked}
                />
                {hasPayDate && (
                    <ExpensePayDate
                        index={index}
                        expense={expense}
                        mode={mode}
                        isPosted={isPosted}
                        isViewOnly={isViewOnly}
                    />
                )}
                <ExpenseCategoryType
                    expenseTypes={expenseTypes}
                    index={index}
                    expense={expense}
                    expenseCategories={expenseCategories}
                    mode={mode}
                    isSGA={isSGA}
                    setIsDivisionDisabled={setIsDivisionDisabled}
                    isPosted={isPosted}
                    isViewOnly={isViewOnly}
                    isInvoiceReviewLocked={isInvoiceReviewLocked}
                />
                {isMiscExpense && (
                    <Grid2 size={{ md: 12 }}>
                        <MiscExpenseDescription
                            index={index}
                            expense={expense}
                            isMiscExpense={isMiscExpense}
                            isRequiredByStatus={creditCardFieldsRequired}
                            isViewOnly={props?.isViewOnly ?? false}
                            isInvoiceReviewLocked={isInvoiceReviewLocked}
                        />
                    </Grid2>
                )}
                {hasTransactionDate && (
                    <ExpenseTransactionDate
                        index={index}
                        expense={expense}
                        mode={mode}
                        isPosted={isPosted}
                        isViewOnly
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                    />
                )}
                {isCreditCardTransaction && (
                    <ExpensePostedDate index={index} expense={expense} isViewOnly />
                )}
                <ExpenseAmount
                    disabled={isCreditCardTransaction && (isCorrectionExpense || !isCreditCardUser)}
                    index={index}
                    expense={expense}
                    originalExpense={originalExpense}
                    mode={mode}
                    isPosted={isPosted}
                    isViewOnly={isViewOnly}
                    isInvoiceReviewLocked={isInvoiceReviewLocked}
                    isCreditCardTransaction={isCreditCardTransaction}
                    updateAllAmounts={updateAllAmounts}
                    size={shouldShowCopyChargeAmount ? 'grow' : undefined}
                />
                {isCreditCardTransaction && (
                    <>
                        {shouldShowCopyChargeAmount && (
                            <CopyIconButtonWithTooltip
                                title="Copy Charge Amount"
                                onClick={copyChargeAmountIntoAllocatedAmount}
                                disabled={!chargeAmountValue}
                            />
                        )}
                        <AllocatedAmount
                            disabled
                            index={index}
                            expense={expense}
                            isViewOnly={isViewOnly}
                            isInvoiceReviewLocked={isInvoiceReviewLocked}
                            size={shouldShowCopyChargeAmount ? 'grow' : undefined}
                        />
                    </>
                )}
                {((isExpenseTravel && !canRemoveTravelDateFields) ||
                    (isSGA[index] && expenseType === ExpenseTypeEnum.LodgingSecurityDeposit)) && (
                    <ExpenseDateRange
                        index={index}
                        expense={expense}
                        mode={mode}
                        isPosted={isPosted}
                        isViewOnly={isViewOnly}
                        required={required}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                    />
                )}
                {!isCreditCardTransaction && (
                    <ExpenseComments
                        index={index}
                        expense={expense}
                        isPosted={isPosted}
                        isViewOnly={isViewOnly}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                    />
                )}
                <ExpenseDescription
                    index={index}
                    expense={expense}
                    isViewOnly={isViewOnly}
                    isInvoiceReviewLocked={isInvoiceReviewLocked}
                />
            </Grid2>
        </>
    );
};

export default withStyles(styles)(PreCodedExpenseDetailsForm);
